interface RecordingIndicatorProps {
  /**
   * Whether THIS customer's voice is going into the recording — which is a
   * different question from whether the room is being recorded, and the two
   * genuinely differ (Q1, va-qa-2026-08). The appointment's room is recorded for
   * the seller's voice whatever the customer answered, so a lit "LIVE" over a
   * customer who declined says something untrue about them — and hiding the badge
   * altogether says something untrue about the consultation. Both states are
   * therefore drawn, and they say different things.
   *
   * Same split the widget makes on its header badge (`recordingMe`).
   */
  voiceIncluded: boolean;
  className?: string;
}

/**
 * Persistent visual indicator shown throughout the call when the consultation is
 * being recorded.
 *
 * Not dismissible — no close button or click handler.
 * Positioned by parent component (ConsultationScreen places it top-left).
 */
export function RecordingIndicator({ voiceIncluded, className }: RecordingIndicatorProps) {
  const label = voiceIncluded ? 'LIVE' : 'RECORDED';
  const description = voiceIncluded
    ? 'Your voice is being recorded and transcribed.'
    : 'This consultation is recorded. Your voice is not.';

  return (
    <div
      data-testid="recording-indicator"
      data-voice-included={voiceIncluded}
      title={description}
      aria-label={description}
      className={`flex items-center gap-1.5 bg-black/60 rounded-full px-2.5 py-1 ${className ?? ''}`}
    >
      <span className="relative flex h-2.5 w-2.5">
        {/* The pulse belongs to the customer's own voice going in; a room recorded
            without them is a fact, not something happening to them. */}
        {voiceIncluded && (
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75" />
        )}
        <span
          className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
            voiceIncluded ? 'bg-blue-500' : 'bg-white/50'
          }`}
        />
      </span>
      <span
        className={`text-xs font-semibold tracking-widest ${
          voiceIncluded ? 'text-white' : 'text-white/70'
        }`}
      >
        {label}
      </span>
    </div>
  );
}
